import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, AlertTriangle, BookOpen, Megaphone, CalendarCheck, Star, EyeOff } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Switch } from "@/components/ui/switch";
import { getDashboardStats } from "@/lib/admin/auth.functions";
import { getSettings, updateSetting } from "@/lib/admin/cms.functions";
import { getAdminToken } from "@/lib/admin/session";

export const Route = createFileRoute("/admin/")({
  head: () => ({ meta: [{ title: "Dashboard · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminDashboard,
});

function AdminDashboard() {
  const fetchStats = useServerFn(getDashboardStats);
  const fetchSettings = useServerFn(getSettings);
  const saveSetting = useServerFn(updateSetting);
  const queryClient = useQueryClient();
  const [stats, setStats] = useState<{
    products: number; outOfStock: number; recipes: number; offers: number; pendingReservations: number;
    featured: number; featuredMax: number;
    recentReservations: { id: string; nome: string; cognome: string; created_at: string }[];
    recentRecipes: { id: string; slug: string; title: string; published_at: string }[];
    recentProducts: { id: string; slug: string; title: string; updated_at: string; featured: boolean; out_of_stock: boolean }[];
  } | null>(null);
  const [hideProducts, setHideProducts] = useState(false);
  const [hideRecipes, setHideRecipes] = useState(false);
  const [busy, setBusy] = useState<"products" | "recipes" | null>(null);
  const [gRating, setGRating] = useState<string>("4,7");
  const [gCount, setGCount] = useState<string>("33");
  const [savingReviews, setSavingReviews] = useState(false);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) return;
    fetchStats({ data: { token } })
      .then((s) => setStats(s as never))
      .catch(() => setStats(null));
    fetchSettings({ data: { token } })
      .then((r) => {
        const map = (r.settings ?? {}) as Record<string, unknown>;
        setHideProducts(map["hide_products"] === true || map["hide_products"] === "true");
        setHideRecipes(map["hide_recipes"] === true || map["hide_recipes"] === "true");
        const rv = map["google_rating"];
        const cv = map["google_reviews_count"];
        if (rv !== undefined && rv !== null) setGRating(String(rv).replace(".", ","));
        if (cv !== undefined && cv !== null) setGCount(String(cv));
      })
      .catch(() => {});
  }, [fetchStats, fetchSettings]);

  async function toggle(kind: "products" | "recipes", next: boolean) {
    const token = getAdminToken(); if (!token) return;
    const key = kind === "products" ? "hide_products" : "hide_recipes";
    const prev = kind === "products" ? hideProducts : hideRecipes;
    (kind === "products" ? setHideProducts : setHideRecipes)(next);
    setBusy(kind);
    try {
      await saveSetting({ data: { token, key, value: next } });
      await queryClient.invalidateQueries({ queryKey: ["public-site-settings"] });
      toast.success(next ? `${kind === "products" ? "Prodotti" : "Ricette"} nascosti dal sito` : `${kind === "products" ? "Prodotti" : "Ricette"} visibili sul sito`);
    } catch (e) {
      (kind === "products" ? setHideProducts : setHideRecipes)(prev);
      toast.error(e instanceof Error ? e.message : "Errore");
    } finally { setBusy(null); }
  }

  async function saveReviews(e: React.FormEvent) {
    e.preventDefault();
    const token = getAdminToken(); if (!token) return;
    const rating = Number(gRating.replace(",", "."));
    const count = Number(gCount);
    if (!Number.isFinite(rating) || rating < 0 || rating > 5) {
      toast.error("Valutazione: numero tra 0 e 5");
      return;
    }
    if (!Number.isInteger(count) || count < 0 || count > 100000) {
      toast.error("Numero recensioni: intero ≥ 0");
      return;
    }
    setSavingReviews(true);
    try {
      await saveSetting({ data: { token, key: "google_rating", value: rating } });
      await saveSetting({ data: { token, key: "google_reviews_count", value: count } });
      await queryClient.invalidateQueries({ queryKey: ["public-site-settings"] });
      toast.success("Dati recensioni Google aggiornati");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Errore");
    } finally {
      setSavingReviews(false);
    }
  }

  const cards = [
    { label: "Prodotti totali", value: stats?.products ?? "—", icon: Package, color: "text-brand-red" },
    { label: "In evidenza", value: stats ? `${stats.featured}/${stats.featuredMax}` : "—", icon: Star, color: "text-amber-500" },
    { label: "Esauriti", value: stats?.outOfStock ?? "—", icon: AlertTriangle, color: "text-amber-600" },
    { label: "Ricette", value: stats?.recipes ?? "—", icon: BookOpen, color: "text-emerald-700" },
    { label: "Offerte attive", value: stats?.offers ?? "—", icon: Megaphone, color: "text-indigo-600" },
    { label: "Prenotazioni in attesa", value: stats?.pendingReservations ?? "—", icon: CalendarCheck, color: "text-brand-red" },
  ];

  return (
    <AdminShell>
      <div className="space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Pannello</p>
          <h1 className="font-display text-3xl text-slate-900 mt-1">Ciao Marco</h1>
          <p className="text-sm text-slate-600 mt-1">Da qui gestisci tutto il sito della Bottega delle Carni da Marco.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cards.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <p className="mt-3 text-3xl font-display text-slate-900">{value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-display text-xl text-slate-900 mb-4">Azioni rapide</h2>
          <div className="flex flex-wrap gap-3">
            <Link to={"/admin/prodotti" as never} className="px-4 py-2 bg-brand-red text-white rounded-md text-sm hover:bg-brand-red-hover">+ Aggiungi prodotto</Link>
            <Link to={"/admin/ricette" as never} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800">+ Aggiungi ricetta</Link>
            <Link to={"/admin/offerte" as never} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50">+ Crea offerta</Link>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <EyeOff className="h-5 w-5 text-slate-700 mt-1" />
            <div>
              <h2 className="font-display text-xl text-slate-900">Visibilità sul sito</h2>
              <p className="text-sm text-slate-600 mt-1">
                Attivando questi interruttori nascondi immediatamente l'intera sezione dal sito pubblico:
                voce di menu, pagine, link nelle altre pagine e nella sitemap. I contenuti restano salvati:
                puoi rimostrarli in qualsiasi momento.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4 rounded-md border border-slate-200 p-4">
              <div>
                <div className="font-medium text-slate-900">Nascondi Prodotti</div>
                <div className="text-xs text-slate-500 mt-1">La pagina /prodotti e tutte le schede prodotto non saranno più visibili. La pagina Abatina resta accessibile.</div>
              </div>
              <Switch checked={hideProducts} disabled={busy === "products"} onCheckedChange={(v) => toggle("products", v)} />
            </label>
            <label className="flex items-center justify-between gap-4 rounded-md border border-slate-200 p-4">
              <div>
                <div className="font-medium text-slate-900">Nascondi Ricette</div>
                <div className="text-xs text-slate-500 mt-1">La pagina /ricette e tutte le schede ricetta non saranno più visibili sul sito.</div>
              </div>
              <Switch checked={hideRecipes} disabled={busy === "recipes"} onCheckedChange={(v) => toggle("recipes", v)} />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <Star className="h-5 w-5 text-amber-500 mt-1" />
            <div>
              <h2 className="font-display text-xl text-slate-900">Recensioni Google</h2>
              <p className="text-sm text-slate-600 mt-1">
                Valori mostrati sopra la sezione recensioni della homepage. Aggiornali qui quando cambiano su Google.
              </p>
            </div>
          </div>
          <form onSubmit={saveReviews} className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-slate-500">Valutazione media Google</span>
              <input
                type="text" inputMode="decimal" value={gRating}
                onChange={(e) => setGRating(e.target.value)}
                className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="4,7"
              />
              <span className="text-[11px] text-slate-500">Da 0 a 5 (es. 4,7)</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-slate-500">Numero di recensioni Google</span>
              <input
                type="number" min={0} step={1} value={gCount}
                onChange={(e) => setGCount(e.target.value)}
                className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="33"
              />
              <span className="text-[11px] text-slate-500">Numero intero</span>
            </label>
            <button
              type="submit" disabled={savingReviews}
              className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {savingReviews ? "Salvataggio…" : "Salva"}
            </button>
          </form>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-display text-lg text-slate-900 mb-3">Ultime prenotazioni</h3>
            {stats?.recentReservations.length ? (
              <ul className="space-y-2 text-sm">
                {stats.recentReservations.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{r.nome} {r.cognome}</span>
                    <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500">Nessuna prenotazione in attesa.</p>}
            <Link to={"/admin/prenotazioni" as never} className="mt-3 inline-block text-xs uppercase tracking-wider text-brand-red hover:underline">Vedi tutte →</Link>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-display text-lg text-slate-900 mb-3">Ultime ricette</h3>
            {stats?.recentRecipes.length ? (
              <ul className="space-y-2 text-sm">
                {stats.recentRecipes.map((r) => (
                  <li key={r.id} className="truncate">{r.title}</li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500">Nessuna ricetta.</p>}
            <Link to={"/admin/ricette" as never} className="mt-3 inline-block text-xs uppercase tracking-wider text-brand-red hover:underline">Gestisci ricette →</Link>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-display text-lg text-slate-900 mb-3">Prodotti aggiornati di recente</h3>
            {stats?.recentProducts.length ? (
              <ul className="space-y-2 text-sm">
                {stats.recentProducts.map((p) => (
                  <li key={p.id} className="flex items-center gap-2">
                    <span className="truncate flex-1">{p.title}</span>
                    {p.featured && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                    {p.out_of_stock && <span className="text-[10px] uppercase px-1 bg-red-100 text-red-700 rounded">OOS</span>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500">Nessun prodotto.</p>}
            <Link to={"/admin/prodotti" as never} className="mt-3 inline-block text-xs uppercase tracking-wider text-brand-red hover:underline">Gestisci prodotti →</Link>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}